import { z } from "zod";

import { isoDateTimeSchema } from "./common";
import { proactiveRulesSchema } from "./proactive-rules";

/** 在写入边界统一存大写 `#RRGGBB`。 */
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/)
  .transform((value) => value.toUpperCase());

export const chatboxFontSchema = z.enum(["theme", "sans", "serif"]);

export const chatboxFontSizeSchema = z.union([
  z.literal(13),
  z.literal(14),
  z.literal(16),
]);

export const chatboxPositionSchema = z.enum(["bottom_right", "bottom_left"]);

/** 全店主动提示形态：独立气泡或拉长角色身体，桌面与移动端共用。 */
export const proactivePromptStyleSchema = z.enum(["bubble", "body"]);

/** 全店角色画法：实色，或主题色渐变加背景模糊的水彩玻璃。主色太浅压不住白眼睛时，挂件自己退回实色。 */
export const chatboxCharacterSchema = z.enum(["solid", "watercolor"]);

/** 挂件面板的底色；亮暗之外的色值由挂件按亮度自己派生。 */
export const chatboxSurfaceSchema = z.enum(["light", "dark"]);

/** 通用圆角档位与 Chatbox 主题原型的圆角基准，直接对应 WidgetPanel 的 `radius`。 */
export const chatboxRadiusSchema = z.union([
  z.literal(0),
  z.literal(2),
  z.literal(8),
  z.literal(10),
  z.literal(12),
  z.literal(16),
  z.literal(20),
  z.literal(999),
]);

/** 挂件的气质：细线、编辑感。对应 WidgetPanel 的 `direction`。 */
export const chatboxDirectionSchema = z.enum(["hairline", "editorial"]);

/**
 * 重复要报在那一条的下标上，别改成数组级的 refine：后台靠下标把错误显示在对应那一行下面。
 */
export const suggestedQuestionsSchema = z
  .array(z.string().trim().min(1).max(80))
  .max(3)
  .superRefine((questions, ctx) => {
    questions.forEach((question, index) => {
      if (questions.indexOf(question) !== index) {
        ctx.addIssue({
          code: "custom",
          message: "Suggested questions must be unique",
          path: [index],
        });
      }
    });
  });

/**
 * 商家配置（PRD 4.6）。Getting Started 和 Settings 读写的是同一份。
 * 只有保存成功的配置才进入店面和 Playground。
 */
export const settingsSchema = z.object({
  /** 新店默认不启用主动消息规则。 */
  proactiveRules: proactiveRulesSchema.default([]),
  agent: z.object({
    /** 买家可见的 AI 名称。 */
    name: z.string().trim().min(1).max(40),
  }),
  chatbox: z.object({
    /** 店面开关；关闭后新页面加载即不再展示挂件。 */
    storefrontEnabled: z.boolean(),
    welcomeMessage: z.string().trim().min(1).max(80),
    /** 首屏建议问题。 */
    suggestedQuestions: suggestedQuestionsSchema,
    themeColor: hexColorSchema,
    font: chatboxFontSchema,
    fontSize: chatboxFontSizeSchema,
    position: chatboxPositionSchema,
    /** 买家能否把收起入口拖到左右两边任意高度；关掉后入口固定在 `position`。 */
    launcherDraggable: z.boolean(),
    /** 每组 AI 回复下方的复制按钮。 */
    replyCopyButton: z.boolean(),
    proactivePromptStyle: proactivePromptStyleSchema,
    surface: chatboxSurfaceSchema,
    radius: chatboxRadiusSchema,
    direction: chatboxDirectionSchema,
    /** 毛玻璃：面板半透明并模糊身后的店面。具体数值由挂件定。 */
    glass: z.boolean(),
    /** 挂件角色画法。 */
    character: chatboxCharacterSchema,
  }),
  handoff: z.object({
    /** 当前 Tenant 唯一支持邮箱；验证状态只由 BFF 提供。 */
    supportEmail: z.email(),
  }),
});

export type Settings = z.infer<typeof settingsSchema>;

/** `PUT /api/settings` 的请求体，整份覆盖。 */
export const settingsRequestSchema = settingsSchema.extend({
  /** Tenant 只能来自商家鉴权，不能由保存请求指定。 */
  tenantId: z.never().optional(),
  tenant_id: z.never().optional(),
});

/** `GET /api/settings` 与 `PUT /api/settings` 的响应；从未保存过时 `updatedAt` 为 null。 */
export const settingsResponseSchema = settingsSchema.extend({
  /**
   * 品牌 Logo 的绝对地址，没传过就是 null。只读：图片不走 JSON，
   * 由 `POST /api/settings/logo` 单独上传，所以请求体里没有这个字段。
   * 收紧成必填会在挂件版本比 BFF 新的时候把整次握手判死（见 GON-38 的教训），
   * 缺键一律当没有 Logo。
   */
  logoUrl: z.url().nullable().default(null),
  /** 新店尚未设置邮箱时允许读出空值；保存请求仍要求有效邮箱。 */
  handoff: z.object({
    supportEmail: z.union([z.email(), z.literal("")]),
    supportEmailVerified: z.boolean().default(false),
    supportEmailVerification: z
      .object({
        status: z.enum(["unsent", "pending", "verified"]),
        lastSentAt: isoDateTimeSchema.nullable(),
        resendAvailableAt: isoDateTimeSchema.nullable(),
      })
      .default({ status: "unsent", lastSentAt: null, resendAvailableAt: null }),
  }),
  updatedAt: isoDateTimeSchema.nullable(),
  /**
   * 主题编辑器深链接，只读，由服务端按店铺和 App 的 client id 拼出来。
   * 只有给后台的 `/api/settings` 响应带它；挂件握手用的是同一份 schema，
   * 那边既没有也用不上，所以是选填——收紧成必填会把版本错位的握手判死（GON-38）。
   */
  themeEditorUrl: z.url().optional(),
});

export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

export const supportEmailResendResponseSchema = z.object({
  outcome: z.enum([
    "sent",
    "cooldown",
    "already_verified",
    "not_saved",
    "unavailable",
    "failed",
  ]),
  settings: settingsResponseSchema,
});

export type SupportEmailResendResponse = z.infer<
  typeof supportEmailResendResponseSchema
>;

/** PRD 给的缺省值，新店第一次打开 Settings 用。 */
export const chatboxDefaults: Settings["chatbox"] = {
  storefrontEnabled: false,
  welcomeMessage: "Hi! What can I help you find today?",
  suggestedQuestions: [],
  themeColor: "#1A73E8",
  font: "theme",
  fontSize: 14,
  position: "bottom_right",
  launcherDraggable: true,
  replyCopyButton: false,
  proactivePromptStyle: "bubble",
  surface: "light",
  radius: 8,
  direction: "hairline",
  glass: false,
  character: "solid",
};
